import { api } from '@/api';
import { CopyButton, DeleteButton, EditButton } from '@/components/common';
import { Page } from '@/components/dashboard/page';
import { Env } from '@/env';
import { useSocket } from '@/hooks';
import type { Socket } from 'socket.io-client';
import type { GameMember, GameRoom, GameState } from '@shared/modelTypes/game-types';
import type { Location } from '@shared/modelTypes/location-types';
import { teamNames, type RoomStatus } from '@shared/modelTypes/session-types';
import { createFileRoute } from '@tanstack/react-router';
import { Badge, Button, Code, Heading, type BadgeProps, ModalButton, Row, Spacer } from 'ht-sdk/react';
import { ClockFading, DoorOpen, Headset, OctagonXIcon, Presentation, SquarePlay, SquarePlus, WifiOff } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { router } from '@/main';
import { TimerBar } from '@/components/dashboard/TimerBar';
import { useTimer } from '@/engine/hooks';
import { formatTime } from '@shared/util';
import { applyServerTime, requestTimeSync, serverNow } from '@/engine/clock';
import { Vars } from '@shared/game-variables';
import { useForm } from 'react-hook-form';
import { Form, resolver, SelectField } from 'ht-sdk/react/forms';
import z from 'zod';
import { toast } from 'sonner';
import type { UserRole } from '@shared/modelTypes/user-types';
import { useSession } from 'ht-sdk/auth/client';
import { auth } from '@/auth';
import { setTag as sentrySetTag } from '@sentry/browser';

const url = new URL(Env.VITE_WEBSOCKET_URL);
const origin = url.origin;
const basePath = url.pathname.replace(/\/$/, '');
const path = `${basePath}`;
const teamColors = {
	alpha: {
		textColor: 'text-purple-300',
		border: 'border border-purple-400',
		bg: 'bg-purple-400/40',
		bgGradient: 'bg-purple-400/40 bg-radial-[at_10%_75%] from-sky-100/45 to-purple-700/40',
	},
	beta: {
		textColor: 'text-yellow-300',
		border: 'border border-yellow-400',
		bg: 'bg-yellow-400/40',
		bgGradient: 'bg-yellow-400/40 bg-radial-[at_10%_75%] from-sky-100/45 to-yellow-700/40',
	},
	delta: {
		textColor: 'text-green-300',
		border: 'border border-green-400',
		bg: 'bg-green-400/40',
		bgGradient: 'bg-green-400/40 bg-radial-[at_10%_75%] from-sky-100/45 to-green-700/40',
	},
	gamma: {
		textColor: 'text-teal-300',
		border: 'border border-teal-400',
		bg: 'bg-teal-400/40',
		bgGradient: 'bg-teal-400/40 bg-radial-[at_10%_75%] from-sky-100/45 to-teal-700/40',
	},
};

export const Route = createFileRoute('/dashboard/locations/$id/view')({
	component: RouteComponent,
	loader: async ({ params }) => {
		const location = await api.locationGetWithSessionList({ id: params.id });
		sentrySetTag('location', params.id);

		return {
			location: location.success ? location.data : null,
		};
	},
});

function RouteComponent() {
	// const { id } = Route.useParams();

	const { location } = Route.useLoaderData();
	const session = useSession(auth);

	if (!location) return <Page>Location not found</Page>;

	const { sessions, ...initialLocation } = location;

	const view = useLocationView(initialLocation);

	return (
		<Page>
			<div className="flex flex-row items-center gap-2">
				<Heading>{location.name}</Heading>
				{!location.sessionId && <Badge status="info">No session</Badge>}
			</div>
			<div className="flex gap-2">
				{view.canCreateSession && (
					<Button iconLeft={SquarePlus} onClick={() => view.createSession()} className="cursor-pointer">
						Create session
					</Button>
				)}
				{view.canStartSession && (
					<Button iconLeft={SquarePlay} onClick={() => view.startSession()} className="cursor-pointer">
						Start Session
					</Button>
				)}
				{view.canStopSession && (
					<Button
						iconLeft={OctagonXIcon}
						onClick={() => view.stopSession()}
						mode={view.canStartSession ? 'secondary' : 'primary'}
						className="cursor-pointer"
					>
						Stop Session
					</Button>
				)}
				{view.canOpenRandomRoom && (
					<Button iconLeft={DoorOpen} onClick={() => view.openRandomRoom()} mode="secondary" className="cursor-pointer">
						Open Random Room
					</Button>
				)}
				<Button
					iconLeft={Presentation}
					as="a"
					href={Env.VITE_GAME_CLIENT_URL + `/observe-game?locationId=${location.id}`}
					target="_blank"
					mode="secondary"
					className="group flex items-center gap-2"
				>
					Observe Game
				</Button>
			</div>

			<LocationInfoView location={location} view={view} sessions={sessions} userRole={session?.role ?? 'user'} />

			{view.location?.sessionId && view.gameState && <GameStateView state={view.gameState} />}

			{view.gameState?.rooms && <RoomsView rooms={view.gameState.rooms} gameState={view.gameState} />}
		</Page>
	);
}

function MemberBadge({ member }: { member: GameMember }) {
	return (
		<span
			key={member.memberId}
			className={`flex size-7 items-center justify-center rounded-full ${teamColors[member.team].bg} font-mono text-sm font-bold`}
		>
			{member.role === 'operator' && <Headset className="size-5" />}
			{member.role === 'tech-team-one' && <>T1</>}
			{member.role === 'tech-team-two' && <>T2</>}
		</span>
	);
}

function RoomsView({ rooms, gameState }: { rooms: GameRoom[]; gameState: GameState }) {
	function remainingMs(room: GameRoom): number {
		switch (room.status) {
			case 'startup': {
				if (room.startupTime == null) return Number.POSITIVE_INFINITY;
				return room.startupTime - serverNow();
			}
			case 'repairing': {
				if (room.endRepairingTime != null) return room.endRepairingTime - serverNow();
				return room.repairTime;
			}
			default:
				return Number.POSITIVE_INFINITY;
		}
	}

	const statusRank: Record<RoomStatus, number> = {
		startup: 0,
		repairing: 1,
		success: 2,
		failed: 2,
		closed: 3,
	};

	const sortedRooms = [...rooms].sort((a, b) => {
		const rankDiff = statusRank[a.status] - statusRank[b.status];
		if (rankDiff !== 0) return rankDiff;

		const remDiff = remainingMs(a) - remainingMs(b);
		if (remDiff !== 0) return remDiff;

		return a.id.localeCompare(b.id);
	});

	const roomColor: Record<RoomStatus, string> = {
		startup: 'text-gray-600',
		repairing: 'text-gray-600',
		success: 'text-green-600',
		failed: 'text-red-600',
		closed: 'text-gray-400',
	};

	return (
		<div className="rounded-lg border border-gray-200 bg-white p-4">
			<Row className="flex flex-col items-center gap-2 sm:flex-row sm:gap-1">
				<Heading mode="tertiary">Rooms</Heading>
				<Spacer />
				<div className="flex flex-row items-center gap-2 text-sm">
					<div className="flex items-center gap-1">
						<small>Startup</small>
						<span className="size-4 rounded-full bg-gradient-to-r from-orange-500 via-red-500 to-red-600" />
					</div>
					<div className="flex items-center gap-1">
						<small>Repairing</small>
						<span className="size-4 rounded-full bg-gradient-to-r from-green-800 via-green-600 to-green-300" />
					</div>
				</div>
			</Row>

			<div className="mt-2 overflow-x-auto">
				<table className="w-full min-w-[400px] table-auto">
					<tbody>
						{sortedRooms.map((room) => {
							const member = room?.assignedMemberId
								? gameState.members.find((m) => m.memberId === room.assignedMemberId)
								: null;

							return (
								<tr key={room.id} className={`border-b border-gray-200 text-sm sm:text-base ${roomColor[room.status]}`}>
									<td className="px-2 py-1">{room.name}</td>
									<td className="px-2 py-1">
										<div className="flex flex-row flex-wrap items-center justify-end gap-1">
											{room.status == 'repairing' && room.repairCodes.map((code) => <Code>{code}</Code>)}
											{member && (
												<>
													<span className="font-bold uppercase">{member.team}</span>
													<MemberBadge member={member} />
												</>
											)}
										</div>
									</td>
									<td className="px-2 py-1">
										{room.status === 'startup' ? (
											<TimerBar
												time={{
													from: room.startupTime!,
													to: room.startRepairingTime!,
													progress: gameState?.gameOverTime,
												}}
												mode="negative"
												height="sm"
											/>
										) : room.status === 'repairing' ? (
											<TimerBar
												time={{
													from: room.startRepairingTime!,
													to: room.endRepairingTime!,
													progress: gameState?.gameOverTime,
												}}
												mode="positive"
												height="sm"
											/>
										) : (
											<TimerBar time={{ from: 0, to: 0, progress: 0 }} mode="negative" height="sm" />
										)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
}

function LocationInfoView({
	location,
	view,
	sessions,
	userRole,
}: {
	location: Location;
	view: any;
	sessions: { id: string; createdAt: Date }[];
	userRole: UserRole;
}) {
	const methods = useForm({
		resolver: resolver(z.object({ id: z.string() })),
	});

	const [pdfLoading, setPdfLoading] = useState(false);
	const timer = useTimer({ from: view?.gameState?.startTime!, to: view?.gameState?.startTime! + Vars.gameDuration });

	const onSubmit = async (data: { id: string }) => {
		setPdfLoading(true);
		const res = await api.generateReport(data);
		if (res.success) {
			const path = res.data;
			const url = new URL(path, window.location.origin);
			const link = document.createElement('a');
			link.href = url.toString();
			link.download = path.split('/').pop() || 'download';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} else {
			toast.error('Unable to create report');
		}
		setPdfLoading(false);
	};

	const isAdmin = userRole == 'admin';

	return (
		<div className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-4">
			<div className="flex flex-row">
				<Heading mode="tertiary">Location info</Heading>
				<Spacer />
				<span className="font-mono">
					{view.gameState?.status == 'running' ? <Heading mode="tertiary">{formatTime(timer.timeLeft)}</Heading> : ''}
				</span>
			</div>
			<div className="flex items-center gap-2">
				<small className="text-gray-600">Code: </small>
				<Code>{location.code}</Code>
			</div>
			<small className="text-gray-600">Location name: {location.name}</small>
			<small className="text-gray-600">Session ID: {location.sessionId ?? 'No session connected'}</small>
			<small className="text-gray-600">
				Time remaining: {view.gameState?.status == 'running' ? formatTime(timer.timeLeft) : 'No running session'}
			</small>
			{view.canCreateSession && <small className="text-theme-500 italic">No session, press "Create session" to create session</small>}
			{view.canStartSession && <small className="text-theme-500 italic">No active session, press "Start session" to start</small>}
			{!view.canStartSession && view.canStopSession && (
				<small className="text-theme-500 italic">Session running, press "Stop session" to stop</small>
			)}
			{sessions.length > 0 && (
				<Form methods={methods} onSubmit={onSubmit}>
					<SelectField
						name="id"
						label="Session"
						options={sessions
							.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
							.map(({ id, createdAt }) => ({
								value: id,
								label: new Date(createdAt).toLocaleString('nl-NL', {
									day: '2-digit',
									month: '2-digit',
									year: 'numeric',
									hour: '2-digit',
									minute: '2-digit',
								}),
							}))}
					/>
					<Button type="submit" className="relative mt-2 cursor-pointer" disabled={pdfLoading}>
						Download pdf
					</Button>
				</Form>
			)}
			<Row gap={1}>
				<Spacer />
				{isAdmin && <EditButton to="/dashboard/locations/$id/edit" params={{ id: location.id }} />}
				{/* TODO: fix as={ModalButton} on inheriting other button props */}
				{isAdmin && (
					<CopyButton
						as={ModalButton}
						onAccept={async () => {
							const result = await api.locationDuplicate({ id: location.id });
							if (result.success) router.navigate({ to: '/dashboard/locations/$id/edit', params: { id: result.data.id } });
						}}
						className="cursor-pointer"
					/>
				)}
				{isAdmin && (
					<DeleteButton
						as={ModalButton}
						onAccept={async () => {
							await api.locationDelete({ id: location.id });
							router.navigate({ to: '/dashboard/locations' });
						}}
						className="cursor-pointer"
					/>
				)}
			</Row>
		</div>
	);
}

function MemberRow({ member, state }: { member: GameMember; state: GameState }) {
	return (
		<div className="flex w-full flex-row items-center gap-2">
			<div className="flex w-fit items-center gap-1 rounded-full border border-gray-200">
				<MemberBadge member={member} />
				{member.role !== 'operator' && <TechTeamDetails member={member} state={state} />}
			</div>
			{member.deviceId ? (
				<>
					<Spacer />
					<DeleteButton
						as={ModalButton}
						onAccept={async () => {
							await api.memberDelete({ sessionId: state.sessionId, memberId: member.memberId });
						}}
						className="cursor-pointer"
					/>
				</>
			) : (
				'No device connected'
			)}
		</div>
	);
}

function TechTeamDetails({ member, state }: { member: GameMember; state: GameState }) {
	const timer = useTimer({
		from: member.connectionTime,
		to: member.connectionTime + Vars.connectionDuration,
		progress: state.gameOverTime,
	});

	const room = state.rooms.find((r) => r.assignedMemberId === member.memberId);

	return (
		<div className="pr-2">
			{room && (
				<span
					className={`size-4 rounded-full bg-gradient-to-r ${
						room.status === 'startup' ? 'from-orange-500 via-red-500 to-red-600' : 'from-green-800 via-green-600 to-green-300'
					}`}
				/>
			)}
			{!member.shiftStarted ? (
				<ClockFading className="size-4 text-gray-600" />
			) : timer.timeLeft ? (
				formatTime(timer.timeLeft)
			) : (
				<WifiOff className="size-4 text-gray-600" />
			)}
		</div>
	);
}

function GameStateView({ state }: { state: GameState }) {
	const status: BadgeProps['status'] = state.status === 'running' ? 'success' : state.status === 'open' ? 'info' : 'warn';

	return (
		<div className="rounded-lg border border-gray-200 bg-white p-4">
			<Row gap={1}>
				<Heading mode="tertiary">
					Game State <Badge status={status}>{state.status}</Badge>
				</Heading>
				<Spacer />
				<div className="flex flex-row items-center gap-2 text-sm">
					<div className="flex items-center gap-1">
						<small>Shift not started</small>
						<ClockFading className="size-4 text-gray-600" />
					</div>
					<div className="flex items-center gap-1">
						<small>Connection lost</small>
						<WifiOff className="size-4 text-gray-600" />
					</div>
					<div className="flex items-center gap-1">
						<small>Connection time</small>
						<span className="text-gray-600">00:00</span>
					</div>
				</div>
			</Row>
			<small className="block pb-2 text-gray-600">Customer satisfaction: {100 - state.systemDamage}%</small>

			<div className="overflow-x-auto">
				<table className="mb-2 w-full min-w-[400px] rounded bg-gray-100 shadow-sm">
					<thead>
						<tr>
							{teamNames.map((name) => (
								<th
									key={name}
									className={`w-[25%] px-2 py-1 text-left text-sm text-gray-700 capitalize sm:text-base ${teamColors[name].bg} ${teamColors[name].border}`}
								>
									{name.toUpperCase()}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						<tr>
							{teamNames.map((name) => {
								const teamState = state.teamStates[name];
								const members = state.members.filter((member) => member.team === name);
								return (
									<td key={name} className="border border-gray-200 bg-white p-2 align-top text-sm sm:text-base">
										<div className="flex flex-col gap-2">
											<div className={`w-fit rounded-full px-2 font-medium ${teamColors[name].bg}`}>
												Score: {teamState?.score}
											</div>

											<div className="flex flex-col gap-1 text-gray-600">
												Members:
												<div className="flex flex-wrap gap-1">
													{members.map((member) => (
														<MemberRow key={member.memberId} member={member} state={state} />
													))}
												</div>
											</div>
										</div>
									</td>
								);
							})}
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	);
}

function useLocationView(initialLocation: Location | null) {
	const [location, setLocation] = useState<Location | null>(initialLocation);
	const [gameState, setGameState] = useState<GameState | null>(null);
	const socketRef = useRef<Socket | null>(null);

	useSocket({
		socketKey: initialLocation?.id,
		url: origin,
		opts: {
			transports: ['websocket'],
			path,
			auth: {
				locationId: initialLocation?.id,
			},
		},
		init: (socket) => {
			socketRef.current = socket;
			console.log('socket initialized', socket);
			socket.on('location-state', (location) => {
				setLocation(location);
			});
			socket.on('game-state', (gameState) => {
				console.log('game state', gameState);
				applyServerTime(gameState.serverTime);
				setGameState(gameState);
			});
			socket.on('update-game-state', (gameState) => {
				console.log('update game state', gameState);
				applyServerTime(gameState.serverTime);
				setGameState(gameState);
			});
			socket.on('disconnect-reason', (message) => {
				console.log('disconnect reason', message);
			});
			socket.on('connect', () => {
				requestTimeSync(socket);
			});
		},
	});

	useEffect(() => {
		const onVisible = () => {
			if (document.visibilityState === 'visible' && socketRef.current) {
				requestTimeSync(socketRef.current);
			}
		};
		document.addEventListener('visibilitychange', onVisible);
		window.addEventListener('pageshow', onVisible);
		return () => {
			document.removeEventListener('visibilitychange', onVisible);
			window.removeEventListener('pageshow', onVisible);
		};
	}, []);

	const createSession = () => {
		if (!location) return;
		api.createSession({ locationId: location.id });
	};

	const startSession = () => {
		if (!gameState) return;
		api.runSession({ sessionId: gameState.sessionId });
	};

	const stopSession = () => {
		if (!gameState) return;
		api.stopSession({ sessionId: gameState.sessionId });
	};

	const openRandomRoom = () => {
		if (!gameState || gameState.status !== 'running') return;
		api.openRandomRoom({ sessionId: gameState.sessionId });
	};

	return {
		location,
		gameState,
		canCreateSession: !location?.sessionId || gameState?.status == 'closed',
		canStartSession: gameState?.status === 'open',
		canStopSession: gameState && gameState?.status !== 'closed',
		canOpenRandomRoom: gameState && gameState?.status === 'running',
		createSession,
		startSession,
		stopSession,
		openRandomRoom,
	};
}
